import {
  PageContentEditor,
  type PageContentEditorBlock,
} from '@app/components/blocks/pageContentEditor/PageContentEditor'
import Column from '@app/components/layout/Column'
import { Text } from '@app/components/core/typography/Text'
import { useTranslations } from '@i18n/useLanguage'
import { useEffect, useMemo, useState } from 'react'
import { EditContainer } from './blocks/shared/EditContainer'
import {
  isOverviewPageContentStepValid,
  mapOverviewTextBlocks,
  contentBlocksFingerprint,
  persistInitiativePageContentBlocks,
} from '@app/pages/dashboard/initiative/shared/initiativeOverviewTextBlocks'
import { FieldEditActions } from './blocks/shared/FieldEditActions'
import { useEditContainerClose } from './blocks/shared/useEditContainerClose'
import { useClient } from '@based/sdk/react'
import { useToast } from '@app/util/hooks/useToast'
import { InitiativeWithId } from '../../../shared/initiativeTypes'
import { Title } from '@app/components/core/typography/Title'
import { SafeHtml } from '@app/components/core/safeHtml/SafeHtml'
import { ContentCollapsible } from '@app/components/blocks/contentCollapsible/ContentCollapsible'

export type PageContentEditProps = {
  initiative: InitiativeWithId
}

const PageContentEditBody = ({
  initiativeId,
  serverBlocks,
  blocks,
  setBlocks,
}: {
  initiativeId: number
  serverBlocks: PageContentEditorBlock[]
  blocks: PageContentEditorBlock[]
  setBlocks: (next: PageContentEditorBlock[]) => void
}) => {
  const common = useTranslations().app.common
  const close = useEditContainerClose()
  if (!close) {
    throw new Error('PageContentEdit requires an EditContainer ancestor')
  }

  const client = useClient()
  const { showFailToast, showSuccessToast } = useToast()

  const save = () => {
    persistInitiativePageContentBlocks(
      (name, payload) => client.call(name, payload),
      initiativeId,
      serverBlocks,
      blocks,
    )
      .then(() => {
        showSuccessToast(common.toastSuccess)
        close()
      })
      .catch(() => {
        showFailToast()
      })
  }

  const cancel = () => {
    setBlocks(serverBlocks)
    close()
  }

  const saveDisabled =
    contentBlocksFingerprint(serverBlocks) ===
      contentBlocksFingerprint(blocks) ||
    !isOverviewPageContentStepValid(blocks)

  return (
    <Column gap="1rem" alignItems="stretch">
      <PageContentEditor
        value={blocks}
        minBlocks={1}
        onValueChange={setBlocks}
      />
      <FieldEditActions
        onCancel={cancel}
        onSave={save}
        saveDisabled={saveDisabled}
      />
    </Column>
  )
}

export const PageContentEdit = ({ initiative }: PageContentEditProps) => {
  const viewTranslations = useTranslations().app.dashboard.initiative.view
  const placeholders =
    useTranslations().app.dashboard.initiative.creation.pageContentPlaceholders

  const serverBlocks = useMemo(
    () =>
      mapOverviewTextBlocks(initiative.contentBlocks, [placeholders.mainBlock]),
    [initiative.contentBlocks, placeholders],
  )

  const [blocks, setBlocks] = useState<PageContentEditorBlock[]>(serverBlocks)

  useEffect(() => {
    setBlocks(serverBlocks)
  }, [serverBlocks])

  return (
    <EditContainer>
      <EditContainer.View>
        <Column gap="1rem" alignItems="stretch">
          {serverBlocks.length === 0 ? (
            <Text.Medium color="var(--palette-blue-500)">
              {viewTranslations.pageContentEmpty}
            </Text.Medium>
          ) : (
            serverBlocks.map((block, blockIdx) => (
              <Column key={block.id ?? `overview-block-${blockIdx}`} gap={0}>
                <Title.H5>{block.title}</Title.H5>
                <ContentCollapsible truncateAfter={5}>
                  <SafeHtml html={block.body} />
                </ContentCollapsible>
              </Column>
            ))
          )}
        </Column>
      </EditContainer.View>
      <EditContainer.Edit>
        <PageContentEditBody
          initiativeId={initiative.id}
          serverBlocks={serverBlocks}
          blocks={blocks}
          setBlocks={setBlocks}
        />
      </EditContainer.Edit>
    </EditContainer>
  )
}
